"use client"

import { useState, useEffect } from "react"
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material"
import {
  ArrowBack as ArrowBackIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material"
import { toast, ToastContainer } from "react-toastify"
import { boxService } from "../services/api" // Import boxService

const AdminReservations = () => {
  const [reservations, setReservations] = useState({})
  const [selectedReservation, setSelectedReservation] = useState(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [filter, setFilter] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")

  // Function to fetch reservations from the backend
  const fetchReservations = async () => {
    try {
      const response = await boxService.getAllBoxes() // Use boxService
      const fetchedBoxes = response.data

      const mappedReservations = fetchedBoxes.reduce((acc, box) => {
        acc[box.name] = {
          status: box.status,
          client: box.client_prenom && box.client_nom ? `${box.client_prenom} ${box.client_nom}` : null,
          cheval: box.cheval_nom,
          prenom: box.client_prenom,
          nom: box.client_nom,
          telephone: box.client_telephone,
          notes: box.notes,
          checkInDate: box.check_in, // Changed from reservation_date
          checkOutDate: box.check_out, // New field
        }
        return acc
      }, {})
      setReservations(mappedReservations)
    } catch (error) {
      console.error("Error fetching reservations:", error)
      toast.error("Erreur lors du chargement des réservations.", { position: "top-center" })
    }
  }

  // Load initial reservations from API
  useEffect(() => {
    fetchReservations()
  }, [])

  const handleEditClick = (boxId) => {
    setSelectedReservation({ boxId, ...reservations[boxId] })
    setEditModalOpen(true)
  }

  const handleDeleteClick = (boxId) => {
    setSelectedReservation({ boxId, ...reservations[boxId] })
    setDeleteModalOpen(true)
  }

  const handleUpdateReservation = async () => {
    if (
      !selectedReservation.prenom ||
      !selectedReservation.nom ||
      !selectedReservation.telephone ||
      !selectedReservation.cheval ||
      !selectedReservation.checkInDate || // New validation
      !selectedReservation.checkOutDate // New validation
    ) {
      toast.error("Veuillez remplir tous les champs obligatoires", {
        position: "top-center",
        autoClose: 3000,
      })
      return
    }

    // Basic date validation: check-out must be after or equal to check-in
    if (new Date(selectedReservation.checkInDate) > new Date(selectedReservation.checkOutDate)) {
      toast.error("La date de départ doit être égale ou postérieure à la date d'arrivée.", {
        position: "top-center",
        autoClose: 3000,
      })
      return
    }

    try {
      const updatedData = {
        status: selectedReservation.status, // Keep current status, admin can change it via dropdown
        client_prenom: selectedReservation.prenom,
        client_nom: selectedReservation.nom,
        client_telephone: selectedReservation.telephone,
        cheval_nom: selectedReservation.cheval,
        notes: selectedReservation.notes,
        check_in: selectedReservation.checkInDate, // Changed
        check_out: selectedReservation.checkOutDate, // New
      }
      await boxService.updateBox(selectedReservation.boxId, updatedData) // Use boxService
      toast.success("Réservation mise à jour avec succès !", {
        position: "top-center",
        autoClose: 3000,
      })
      fetchReservations() // Re-fetch data to update UI
      setEditModalOpen(false)
      setSelectedReservation(null)
    } catch (error) {
      console.error("Error updating reservation:", error)
      toast.error("Erreur lors de la mise à jour de la réservation.", { position: "top-center" })
    }
  }

  const handleDeleteReservation = async () => {
    try {
      // When deleting, set status to 'available' and clear all details
      await boxService.updateBox(selectedReservation.boxId, { status: "available" })
      toast.success("Réservation supprimée avec succès !", {
        position: "top-center",
        autoClose: 3000,
      })
      fetchReservations() // Re-fetch data to update UI
      setDeleteModalOpen(false)
      setSelectedReservation(null)
    } catch (error) {
      console.error("Error deleting reservation:", error)
      toast.error("Erreur lors de la suppression de la réservation.", { position: "top-center" })
    }
  }

  const handleStatusChange = async (boxId, newStatus) => {
    try {
      const currentBox = reservations[boxId]
      let updatePayload = { status: newStatus }

      // If status changes to 'reserved' or 'pending', ensure existing details are sent
      if (newStatus === "reserved" || newStatus === "pending") {
        if (
          !currentBox.prenom ||
          !currentBox.nom ||
          !currentBox.telephone ||
          !currentBox.cheval ||
          !currentBox.checkInDate ||
          !currentBox.checkOutDate
        ) {
          toast.error(
            "Veuillez remplir tous les champs obligatoires avant de passer au statut 'Réservé' ou 'En attente'.",
            {
              position: "top-center",
              autoClose: 5000,
            },
          )
          // Revert the UI selection if validation fails
          fetchReservations() // Re-fetch to reset the dropdown
          return
        }
        // Basic date validation: check-out must be after or equal to check-in
        if (new Date(currentBox.checkInDate) > new Date(currentBox.checkOutDate)) {
          toast.error("La date de départ doit être égale ou postérieure à la date d'arrivée.", {
            position: "top-center",
            autoClose: 5000,
          })
          fetchReservations() // Re-fetch to reset the dropdown
          return
        }

        updatePayload = {
          ...updatePayload,
          client_prenom: currentBox.prenom,
          client_nom: currentBox.nom,
          client_telephone: currentBox.telephone,
          cheval_nom: currentBox.cheval,
          notes: currentBox.notes,
          check_in: currentBox.checkInDate,
          check_out: currentBox.checkOutDate,
        }
      }
      // Backend controller handles clearing details if status is not 'reserved' or 'pending'

      await boxService.updateBox(boxId, updatePayload) // Use boxService
      toast.success("Statut mis à jour avec succès !", {
        position: "top-center",
        autoClose: 3000,
      })
      fetchReservations() // Re-fetch data to update UI
    } catch (error) {
      console.error("Error updating status:", error)
      toast.error("Erreur lors de la mise à jour du statut.", { position: "top-center" })
    }
  }

  const filteredReservations = Object.entries(reservations).filter(([boxId, reservation]) => {
    // Filter by status
    if (filter !== "all" && reservation.status !== filter) return false

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      return (
        boxId.toLowerCase().includes(searchLower) ||
        (reservation.client && reservation.client.toLowerCase().includes(searchLower)) ||
        (reservation.cheval && reservation.cheval.toLowerCase().includes(searchLower)) ||
        (reservation.telephone && reservation.telephone.includes(searchTerm))
      )
    }
    return true
  })

  const getBoxColor = (status) => {
    switch (status) {
      case "available":
        return "#4CAF50"
      case "reserved":
        return "#F44336"
      case "pending":
        return "#FF9800"
      case "maintenance":
        return "#9E9E9E"
      default:
        return "#4CAF50"
    }
  }

  const statusOptions = [
    { value: "reserved", label: "Réservé" },
    { value: "available", label: "Disponible" },
    { value: "maintenance", label: "Maintenance" },
    { value: "pending", label: "En attente" },
  ]

  return (
    <div className="app">
      <ToastContainer />
      <AppBar position="fixed" sx={{ backgroundColor: "#003366" }}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => window.history.back()}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontSize: { xs: "0.9rem", sm: "1.25rem" },
              color: "#ffffff",
              textAlign: "center",
            }}
          >
            Administration des Réservations
          </Typography>
          <IconButton color="inherit">
            <AdminPanelSettingsIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          padding: { xs: "0.5rem", sm: "1rem" },
          paddingTop: "80px",
          paddingBottom: "20px",
          backgroundColor: "#f5f5f5",
          minHeight: "100vh",
        }}
      >
        <Card sx={{ marginBottom: "1rem", padding: { xs: "0.5rem", sm: "1rem" } }}>
          <Typography variant="h6" sx={{ marginBottom: "1rem" }}>
            Filtres
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select value={filter} onChange={(e) => setFilter(e.target.value)} label="Statut">
                  <MenuItem value="all">Tous</MenuItem>
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={9}>
              <TextField
                fullWidth
                label="Rechercher (box, client, cheval, téléphone)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
          </Grid>
        </Card>

        <Card>
          <CardContent sx={{ padding: "0 !important" }}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#003366" }}>
                    <TableCell sx={{ color: "white" }}>Box</TableCell>
                    <TableCell sx={{ color: "white" }}>Client</TableCell>
                    <TableCell sx={{ color: "white" }}>Cheval</TableCell>
                    <TableCell sx={{ color: "white" }}>Téléphone</TableCell>
                    <TableCell sx={{ color: "white" }}>Date d'arrivée</TableCell> {/* Changed */}
                    <TableCell sx={{ color: "white" }}>Date de départ</TableCell> {/* New */}
                    <TableCell sx={{ color: "white" }}>Statut</TableCell>
                    <TableCell sx={{ color: "white" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredReservations.length > 0 ? (
                    filteredReservations.map(([boxId, reservation]) => (
                      <TableRow key={boxId}>
                        <TableCell>{boxId}</TableCell>
                        <TableCell>{reservation.client || "-"}</TableCell>
                        <TableCell>{reservation.cheval || "-"}</TableCell>
                        <TableCell>{reservation.telephone || "-"}</TableCell>
                        <TableCell>{reservation.checkInDate || "-"}</TableCell> {/* Changed */}
                        <TableCell>{reservation.checkOutDate || "-"}</TableCell> {/* New */}
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={reservation.status}
                              onChange={(e) => handleStatusChange(boxId, e.target.value)}
                              sx={{
                                backgroundColor: getBoxColor(reservation.status),
                                color: "white",
                                "& .MuiSelect-icon": { color: "white" },
                              }}
                            >
                              {statusOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <IconButton onClick={() => handleEditClick(boxId)} color="primary">
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteClick(boxId)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        {" "}
                        {/* Adjusted colspan */}
                        Aucune réservation trouvée
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Edit Reservation Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier la réservation du Box {selectedReservation?.boxId}</DialogTitle>
        <DialogContent>
          {selectedReservation && (
            <Grid container spacing={2} sx={{ marginTop: "0.5rem" }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prénom *"
                  value={selectedReservation.prenom || ""}
                  onChange={(e) =>
                    setSelectedReservation({
                      ...selectedReservation,
                      prenom: e.target.value,
                    })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nom *"
                  value={selectedReservation.nom || ""}
                  onChange={(e) =>
                    setSelectedReservation({
                      ...selectedReservation,
                      nom: e.target.value,
                    })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Téléphone *"
                  type="tel"
                  value={selectedReservation.telephone || ""}
                  onChange={(e) =>
                    setSelectedReservation({
                      ...selectedReservation,
                      telephone: e.target.value,
                    })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nom du cheval *"
                  value={selectedReservation.cheval || ""}
                  onChange={(e) =>
                    setSelectedReservation({
                      ...selectedReservation,
                      cheval: e.target.value,
                    })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {" "}
                {/* New Grid item for check-in */}
                <TextField
                  fullWidth
                  label="Date d'arrivée *"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={selectedReservation.checkInDate || ""}
                  onChange={(e) =>
                    setSelectedReservation({
                      ...selectedReservation,
                      checkInDate: e.target.value,
                    })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {" "}
                {/* New Grid item for check-out */}
                <TextField
                  fullWidth
                  label="Date de départ *"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={selectedReservation.checkOutDate || ""}
                  onChange={(e) =>
                    setSelectedReservation({
                      ...selectedReservation,
                      checkOutDate: e.target.value,
                    })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes"
                  value={selectedReservation.notes || ""}
                  onChange={(e) =>
                    setSelectedReservation({
                      ...selectedReservation,
                      notes: e.target.value,
                    })
                  }
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button onClick={() => setEditModalOpen(false)} color="inherit">
            Annuler
          </Button>
          <Button onClick={handleUpdateReservation} variant="contained" sx={{ backgroundColor: "#003366" }}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          Êtes-vous sûr de vouloir supprimer la réservation du Box {selectedReservation?.boxId} ? Cela rendra le box
          disponible.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)} color="inherit">
            Annuler
          </Button>
          <Button onClick={handleDeleteReservation} variant="contained" color="error">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default AdminReservations
